import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";

import {
  GoogleMap,
  useLoadScript,
  Marker,
} from "@react-google-maps/api";

import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";

import { GC_MAP_KEY } from "../../utils/config/config";

const libraries = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "500px",
};

const defaultCenter = {
  lat: 28.6139,
  lng: 77.209,
};

export default function LocationSearchWithMap({
  defaultAddress = {},
  onAddress = () => { },
}) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GC_MAP_KEY,
    libraries,
  });

  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const autocompleteContainerRef = useRef(null);
  const autocompleteElementRef = useRef(null);

  // ---------------------------------------------------------
  // Extract address details
  // ---------------------------------------------------------

  useEffect(() => {
    if (!autocompleteElementRef.current) return;

    autocompleteElementRef.current.style.colorScheme = "light";
  }, [isLoaded, autocompleteElementRef.current]);

  const extractAddressDetails = useCallback(
    (place, callOnAddress = true) => {
      if (!place) return;

      const components = place.addressComponents || [];

      const getComponent = (type) => {
        const component = components.find((c) =>
          c.types?.includes(type)
        );

        return component?.longText || "";
      };

      const lat = place.location?.lat() || 0;
      const lng = place.location?.lng() || 0;

      if (callOnAddress) {
        onAddress({
          addressLine1: place.formattedAddress || "",

          city: getComponent("locality"),

          state: getComponent(
            "administrative_area_level_1"
          ),

          province: getComponent(
            "administrative_area_level_2"
          ),

          country: getComponent("country"),

          pincode: getComponent("postal_code"),

          latitude: lat,
          longitude: lng,
        });
      }
    },
    [onAddress]
  );

  // ---------------------------------------------------------
  // New Google Places Autocomplete
  // ---------------------------------------------------------

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) {
      return;
    }

    if (!autocompleteContainerRef.current) {
      return;
    }

    // Avoid creating it multiple times
    if (autocompleteElementRef.current) {
      return;
    }

    const autocomplete =
      new window.google.maps.places.PlaceAutocompleteElement();

    // Optional: restrict to India
    autocomplete.includedRegionCodes = ["in"];

    // Optional placeholder
    autocomplete.placeholder = "Search location";

    autocompleteContainerRef.current.appendChild(
      autocomplete
    );

    autocompleteElementRef.current = autocomplete;

    // -------------------------------------------------------
    // New API event
    // -------------------------------------------------------

    const handlePlaceSelect = async (event) => {
      try {
        const placePrediction = event.placePrediction;

        if (!placePrediction) {
          return;
        }

        const place = placePrediction.toPlace();

        await place.fetchFields({
          fields: [
            "displayName",
            "formattedAddress",
            "addressComponents",
            "location",
          ],
        });

        if (!place.location) {
          return;
        }

        const lat = place.location.lat();
        const lng = place.location.lng();

        setMapCenter({
          lat,
          lng,
        });

        setSelectedLocation({
          lat,
          lng,
        });

        extractAddressDetails(place);
      } catch (error) {
        console.error(
          "Place selection error:",
          error
        );
      }
    };

    autocomplete.addEventListener(
      "gmp-select",
      handlePlaceSelect
    );

    return () => {
      autocomplete.removeEventListener(
        "gmp-select",
        handlePlaceSelect
      );

      if (
        autocompleteContainerRef.current?.contains(
          autocomplete
        )
      ) {
        autocompleteContainerRef.current.removeChild(
          autocomplete
        );
      }

      autocompleteElementRef.current = null;
    };
  }, [isLoaded, extractAddressDetails]);

  // ---------------------------------------------------------
  // Map click → Reverse geocoding
  // ---------------------------------------------------------

  const handleMapClick = useCallback(
    (event) => {
      if (!isLoaded || !window.google?.maps) {
        return;
      }

      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      setSelectedLocation({
        lat,
        lng,
      });

      setMapCenter({
        lat,
        lng,
      });

      const geocoder =
        new window.google.maps.Geocoder();

      geocoder.geocode(
        {
          location: {
            lat,
            lng,
          },
        },
        (results, status) => {
          if (
            status === "OK" &&
            results?.length > 0
          ) {
            const result = results[0];

            // Convert legacy Geocoder result
            // into our address structure
            const components =
              result.address_components || [];

            const getComponent = (type) =>
              components.find((c) =>
                c.types.includes(type)
              )?.long_name || "";

            onAddress({
              addressLine1:
                result.formatted_address || "",

              city: getComponent("locality"),

              state: getComponent(
                "administrative_area_level_1"
              ),

              province: getComponent(
                "administrative_area_level_2"
              ),

              country: getComponent("country"),

              pincode:
                getComponent("postal_code"),

              latitude: lat,
              longitude: lng,
            });
          }
        }
      );
    },
    [isLoaded, onAddress]
  );

  // ---------------------------------------------------------
  // Default address
  // ---------------------------------------------------------

  useEffect(() => {
    if (!isLoaded || !window.google?.maps) {
      return;
    }

    if (
      defaultAddress?.latitude &&
      defaultAddress?.longitude
    ) {
      const lat = Number(
        defaultAddress.latitude
      );

      const lng = Number(
        defaultAddress.longitude
      );

      setMapCenter({
        lat,
        lng,
      });

      setSelectedLocation({
        lat,
        lng,
      });
    }
  }, [defaultAddress, isLoaded]);

  // ---------------------------------------------------------
  // Loading / Error
  // ---------------------------------------------------------

  if (loadError) {
    return (
      <Box>
        Failed to load Google Maps.
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box>
        Loading...
      </Box>
    );
  }


  // ---------------------------------------------------------
  // UI
  // ---------------------------------------------------------

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* New Google Places Autocomplete */}
      <Box
        ref={autocompleteContainerRef}
        sx={{
          width: "100%",
          backgroundColor: "white",
          "& gmp-place-autocomplete": {
            width: "100%",
          },
        }}
      />

      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={14}
        center={mapCenter}
        onClick={handleMapClick}
      >
        {selectedLocation && (
          <Marker
            position={selectedLocation}
          />
        )}
      </GoogleMap>
    </Box>
  );
}